import { MinusCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { FC, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/app/hooks';
import { RootState } from '../../../../../redux/app/store';
import { updateBucket } from '../../../../../redux/features/buckets/BucketsSlice';
import { BucketItem, BucketType } from '../../../../../redux/features/buckets/BucketsState';
import { resetIntelligentFilteringMessage } from '../../../../../redux/features/conversations/conversationsSlice';
import { getIcon } from '../../../../utils/DocumentUtils';
import { BucketTag } from './BucketTag';

export interface IDocumentsToolbarProps {
    chatId: string;
    isShowBucketMenu: boolean;
    onShowBucketMenu: (event) => void;
}

export const BucketsToolbar: FC<IDocumentsToolbarProps> = ({ onShowBucketMenu, isShowBucketMenu, chatId }) => {
    const { Buckets } = useAppSelector((state: RootState) => state.buckets);
    const [selectedBuckets, setSelectedBuckets] = useState<BucketItem[]>([]);
    const dispatch = useAppDispatch();

    useEffect(() => {
        // Filter the array to only include selected items
        const selectedItems = Buckets.filter((item) => item.selected);
        // Create a set of parent ids for selected items
        const parentIds = new Set<string>();
        selectedItems.forEach((item) => {
            const parent = Buckets.find((b) => b.id === item.parent?.id);
            if (parent && parent.selected) {
                parentIds.add(parent.id);
            }
        });

        // Filter out items that are children of selected parents
        const result = selectedItems.filter((item) => !parentIds.has(item.parent?.id ?? ''));

        setSelectedBuckets(result);
    }, [Buckets]);

    const getSelectedDocumentsCount = () => {
        return Buckets.filter((item) => item.selected && item.type === BucketType.BucketSource).length;
    };

    const onRemove = (bucketSource: BucketItem) => {
        dispatch(resetIntelligentFilteringMessage({ chatId }));

        const bucketExist = Buckets.find((f) => f.id === bucketSource.id);

        if (bucketExist) {
            dispatch(updateBucket({ ...bucketExist, selected: false }));
        }
    };

    const defaultBucketButtonMessage: string = isShowBucketMenu
        ? 'Close the file options menu'
        : 'Add files or buckets for better results';
    const expandBucketButtonMessage: string = isShowBucketMenu ? 'Close' : 'More';

    return (
        <div className="flex flex-wrap mb-2">
            <button
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation(); // window click event closes the menu if open so we need to stop that

                    onShowBucketMenu(event);
                }}
                className={`flex items-center mr-1 mb-1 bg-primary-500 text-slate-200 shadow-md rounded-md dark:text-slate-200 dark:bg-primary-500 hover:bg-primary-700 cursor-pointer p-2`}>
                <div className={`text-md pr-2`}>
                    <span className="hidden md:inline-block">
                        {selectedBuckets.length > 0 ? expandBucketButtonMessage : defaultBucketButtonMessage}
                    </span>
                    <span className="md:hidden">
                        {getSelectedDocumentsCount() > 0
                            ? `${getSelectedDocumentsCount()} document${getSelectedDocumentsCount() > 1 ? 's' : ''} selected`
                            : defaultBucketButtonMessage}
                    </span>
                </div>
                <div className="hover:text-red-500">
                    {isShowBucketMenu ? (
                        <MinusCircleIcon
                            className="text-slate-200 dark:text-slate-200 h-5 w-5 hover:text-primary-dark transition-all"
                            strokeWidth="2"
                        />
                    ) : (
                        <PlusCircleIcon
                            className="text-slate-200 dark:text-slate-200 h-5 w-5 hover:text-primary-dark transition-all"
                            strokeWidth="2"
                        />
                    )}
                </div>
            </button>
            {selectedBuckets.length > 0 &&
                selectedBuckets.map((b, i) => (
                    <BucketTag
                        key={`${i}`}
                        icon={getIcon(b.documentType)}
                        title={b.title}
                        onRemove={() => onRemove(b)}
                    />
                ))}
        </div>
    );
};
